import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { TextArea } from 'components/core';
import { useRecoilValue } from 'recoil';
import { currentVisualizationState } from 'states';

import { QUERY_FIELD_DOCS } from 'constants/analyticsConfigDocs';
import { UI_TEXT } from 'constants/appConstants';
import styles from './VisualizationTabs.module.css';
import { FieldTooltip } from 'molecules/fieldTooltip/FieldTooltip';
import { DEFAULT_QUERY_TEMPLATE, QUERY_TEMPLATES } from 'constants/analyticsTemplateDocs';

interface DataProps {
  register: any;
  setValue: any;
  formState: any;
  invalidQuery: boolean;
  isExisting?: boolean;
  chartType?: string;
}

const handleQueryChange = (setValue: any) => (e: any) => {
  setValue('queries', e.target.value, { shouldValidate: true });
};

export const Data = ({
  register,
  setValue,
  formState,
  invalidQuery,
  isExisting = false,
  chartType = '',
}: DataProps) => {
  const { t } = useTranslation();
  const currentVisualization = useRecoilValue(currentVisualizationState);

  // ── Load query template based on selected chart type ──────────────────────
  useEffect(() => {
    if (!isExisting) {
      const template = QUERY_TEMPLATES[chartType?.toLowerCase()] ?? DEFAULT_QUERY_TEMPLATE;
      setValue('queries', template);
    }
  }, [currentVisualization, chartType]);

  const { errors } = formState;

  const chartTypeLabel = chartType
    ? chartType.charAt(0).toUpperCase() + chartType.slice(1).toLowerCase()
    : null;

  return (
    <div style={{ paddingBottom: '40px' }} className="col-sm-12 col-md-12 col-lg-8 mt-4 mb-3">

      {/* ── Field reference panel ─────────────────────────────────────────── */}
      <div className={styles.fieldRefPanel}>
        <div className={styles.fieldRefHeader}>
          <span className="material-icons" style={{ fontSize: 15, color: 'var(--primary-1)' }}>
            menu_book
          </span>
          <span className={styles.fieldRefTitle}>{UI_TEXT.VISUALIZATION.QUERY_REF_TITLE}</span>
          <span className={styles.fieldRefSub}>{UI_TEXT.VISUALIZATION.QUERY_REF_SUB}</span>
        </div>
        <div className={styles.fieldRefGrid}>
          {QUERY_FIELD_DOCS.map((doc) => (
            <div key={doc.field} className={styles.fieldRefItem}>
              <div className={styles.fieldRefItemTop}>
                <code className={styles.fieldRefCode}>{doc.label}</code>
                <FieldTooltip
                  title={doc.label}
                  description={doc.description}
                  example={doc.example}
                  note={doc.note}
                />
              </div>
              <div className={styles.fieldRefItemDesc}>{doc.description.slice(0, 60)}…</div>
            </div>
          ))}
        </div>
      </div>

      {/* ── Query editor ──────────────────────────────────────────────────── */}
      <div className="mb-4">
        <div className={styles.fieldLabelRow}>
          <span className={styles.fieldLabel}>
            {t('VISUALIZATION.FIELD_QUERY')} <span className={styles.required}>*</span>
          </span>
          <FieldTooltip
            title={UI_TEXT.VISUALIZATION.QUERY_TOOLTIP_TITLE}
            description={UI_TEXT.VISUALIZATION.QUERY_TOOLTIP_DESC}
            example={UI_TEXT.VISUALIZATION.QUERY_TOOLTIP_EXAMPLE}
            note={UI_TEXT.VISUALIZATION.QUERY_TOOLTIP_NOTE}
          />
        </div>

        {/* Starter template info note — only for new visualizations */}
        {!isExisting && (
          <div className={styles.jsonNote}>
            <span className="material-icons" style={{ fontSize: 16, flexShrink: 0 }}>
              info
            </span>
            <span>
              <strong>New visualization</strong>
              {chartTypeLabel
                ? ` — ${UI_TEXT.VISUALIZATION.TEMPLATE_NOTE_CHART} `
                : ` — ${UI_TEXT.VISUALIZATION.TEMPLATE_NOTE_PREFIX}. `}
              {chartTypeLabel && <strong>{chartTypeLabel}</strong>}
              {'. '}
              {UI_TEXT.VISUALIZATION.TEMPLATE_NOTE_QUERY}
            </span>
          </div>
        )}

        <TextArea
          rows={12}
          showTitle={false}
          title=""
          {...register('queries')}
          className={`${errors.queries || invalidQuery ? 'is-invalid' : ''}`}
          onChange={handleQueryChange(setValue)}
        />
        <div className="invalid-feedback">
          {errors.queries?.message || t('VALIDATION.INVALID_JSON')}
        </div>
      </div>
    </div>
  );
};